import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { getRoleDefinitions } from '@/features/roles/api';
import { authStore } from '@/services/auth/store';
import { Role as StandardRole } from '@customerio/hydra-permissions/roles';
import type { WorkspacePermission } from '@customerio/hydra-permissions';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';

type ApiPermission = Record<number, WorkspacePermission>;

type ApiRoleDefinition = {
  id: number;
  name: string;
  permission_ids: number[];
};

type RoleDefinition = {
  id: number;
  name: string;
  workspacePermissions: Set<WorkspacePermission>;
};

export function useRoleDefinitions(): Record<number, RoleDefinition> | null {
  const auth = useStore(authStore);

  const rolesQuery = useQuery<
    { role_definitions: ApiRoleDefinition[]; permissions: ApiPermission } | null,
    unknown,
    { role_definitions: ApiRoleDefinition[]; permissions: ApiPermission } | null
  >({
    queryKey: ['role_definitions', auth.account?.id ?? 0],
    queryFn: async () => {
      if (auth.account == null) {
        return null;
      }

      return await getRoleDefinitions(auth.account.id);
    },
    staleTime: Infinity,
  });

  if (rolesQuery.data == null) {
    return null;
  }

  const { role_definitions, permissions } = rolesQuery.data;

  return role_definitions.reduce((acc, roleDefinition) => {
    acc[roleDefinition.id] = {
      id: roleDefinition.id,
      name: roleDefinition.name,
      workspacePermissions: new Set(roleDefinition.permission_ids.map((permissionId) => permissions[permissionId])),
    };
    return acc;
  }, {} as Record<number, RoleDefinition>);
}

export function useUserRoleForCurrentWorkspace(): { name: string; redacted: boolean } | null {
  const auth = useStore(authStore);
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const userRoles = auth.user?.roles ?? null;

  if (userRoles == null) {
    return null;
  }

  const userWorkspaceRole = userRoles.find((role) => role.workspace_id === workspaceId);

  if (userWorkspaceRole == null) {
    return null;
  }

  return {
    name: userWorkspaceRole.role_name,
    redacted: userWorkspaceRole.role_name.includes('-redact-on'),
  };
}

export function isStandardRole(role: string): role is StandardRole {
  return Object.values<string>(StandardRole).includes(role);
}
